import { formatVnd, formatVndExact } from "procurement/design/format/money"
import { formatDate as formatDateDisplay, formatDateTime as formatDateTimeDisplay } from "procurement/design/format/dates"
import { ALLOWED_PAYMENT_TYPES } from "./fieldRules"

export const REGION_OPTIONS = [
  { label: "North", value: "North" },
  { label: "South", value: "South" },
  { label: "Central", value: "Central" },
]

export const REQUESTER_REGION_OPTIONS = [
  { label: "North", value: "North" },
  { label: "South", value: "South" },
]

export const URGENCY_OPTIONS = [
  { label: "Normal", value: "normal" },
  { label: "Urgent", value: "urgent" },
]

export const PAYMENT_METHOD_OPTIONS = [
  { label: "Bank transfer", value: "bank_transfer" },
  { label: "Cash", value: "cash" },
]

// Order Request line-item "Type" — new purchase vs. replacing an existing
// asset. Replacement requires `replacedAssetCode` on that line.
export const ITEM_TYPE_OPTIONS = [
  { label: "New", value: "new" },
  { label: "Replacement", value: "replacement" },
]

// Terminology per the F3 create-flow redesign: values are unchanged so
// existing rows/records keep meaning, only the labels read more plainly.
// "deposit" keeps its special validation meaning (invoice number optional);
// the rest are additive, clearer synonyms for what "normal" used to cover.
export const PAYMENT_TYPE_OPTIONS = [
  { label: "Normal Payment", value: "normal" },
  { label: "Deposit", value: "deposit" },
  { label: "Advance Payment", value: "advance" },
  { label: "Reimbursement", value: "reimbursement" },
  { label: "Refund", value: "refund" },
  { label: "Other", value: "other" },
]

// For create/edit pickers: only the types the payment_type DB CHECK accepts are pickable.
export const PAYMENT_TYPE_PICKER_OPTIONS = PAYMENT_TYPE_OPTIONS.map((o) => ({
  ...o,
  disabled: !ALLOWED_PAYMENT_TYPES.includes(o.value),
}))

export const DOC_TYPES = [
  { label: "Contract", value: "contract" },
  { label: "Invoice/Receipt", value: "invoice" },
  { label: "Delivery note", value: "delivery_note" },
  { label: "Acceptance certificate", value: "acceptance_certificate" },
  { label: "Payment request", value: "payment_request" },
  { label: "Budget Approval", value: "budget_approval" },
  { label: "Price Approval", value: "price_approval" },
  { label: "Others", value: "other" },
]

// docType is free text on the real order-requests/{id}/documents upload API
// (no server-side enum) — this is just the FE's preset picker.
export const ORDER_DOC_TYPE_OPTIONS = [
  { label: "Quote", value: "quote" },
  { label: "Invoice", value: "invoice" },
  { label: "Contract", value: "contract" },
  { label: "Delivery Note", value: "delivery_note" },
  { label: "Additional Info", value: "info" },
  { label: "Image Proof", value: "image_proof" },
  { label: "Other", value: "other" },
]

// Sentinel option value rendered at the end of an Order Line's Item dropdown
// so a requester can name an item that isn't in the catalog yet, instead of
// being blocked on it — see OrderLineRow's handleItemChange. Kept here (not
// inlined) so both OrderLineRow and OrderRequestFormContainer's validation
// agree on the exact same marker.
export const CUSTOM_ITEM_VALUE = "__custom_item__"

// Mirrors the server's whitelist/size gate on POST .../documents so obviously
// invalid files fail fast client-side instead of round-tripping a 400.
export const ORDER_DOC_MAX_SIZE_BYTES = 10 * 1024 * 1024
export const ORDER_DOC_ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "csv",
  // Images — backend now accepts these too.
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "heic",
]

// Delivery confirmation on an approved OR (POST .../order-requests/{id}/delivery).
// `reasonCode` has no dedicated column server-side — it's tagged onto the
// action-log note and carried in full in the outbox payload — so this list is
// FE-owned and must stay in sync with the backend's accepted values.
export const DELIVERY_REJECT_REASON_OPTIONS = [
  { value: "wrong_item", label: "Wrong item delivered" },
  { value: "exchange", label: "Needs to be exchanged" },
  { value: "wrong_specs", label: "Does not match specs" },
  { value: "damaged", label: "Damaged on arrival" },
  { value: "shortage", label: "Short delivery (missing items)" },
  { value: "wrong_quantity", label: "Wrong quantity" },
  { value: "quality", label: "Quality not acceptable" },
  { value: "other", label: "Other" },
]

// PO statuses that still accept a delivery confirmation from the requester.
// Who gets emailed when a delivery has a problem (rejected, or a missing/excess
// line). The backend defaults to "pro" when nothing is sent.
export const DELIVERY_NOTIFY_OPTIONS = [
  { value: "pro", label: "Send to Procurement only" },
  { value: "vendor,pro", label: "Send to supplier + cc Procurement" },
]

export const PO_OPEN_FOR_DELIVERY = ["issued", "receiving"]

// ── Procurement V2 (additive) ───────────────────────────────────────────────
// Organisation scope options used by the manager filter-bar and the admin
// approval-rule builder. Values are stable codes; labels match existing pages.

export const CENTER_OPTIONS = [
  { label: "HN-01 Cau Giay", value: "HN-01", region: "North" },
  { label: "HN-02 Ha Dong", value: "HN-02", region: "North" },
  { label: "HCM-01 District 1", value: "HCM-01", region: "South" },
  { label: "HCM-02 Thu Duc", value: "HCM-02", region: "South" },
  { label: "DN-01 Hai Chau", value: "DN-01", region: "Central" },
]

export const DEPARTMENT_OPTIONS = [
  { label: "Operations", value: "operations" },
  { label: "Academic", value: "academic" },
  { label: "Marketing", value: "marketing" },
  { label: "Facilities", value: "facilities" },
  { label: "IT", value: "it" },
  { label: "Finance", value: "finance" },
]

// Fiscal month filter for the (editable) Manager Budget screen. "" ("Full
// year") selects the whole-year pro.budget row (fiscal_month IS NULL) rather
// than summing the 12 monthly rows.
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

export const FISCAL_MONTH_OPTIONS = [
  { label: "Full year", value: "" },
  ...Array.from({ length: 12 }, (_, i) => ({
    label: MONTH_NAMES[i],
    value: String(i + 1),
  })),
]

export const ORDER_FOR_TYPE = { ONE_MONTH: "ONE_MONTH", MULTIPLE_MONTHS: "MULTIPLE_MONTHS" }

export const ORDER_FOR_OPTIONS = [
  { value: ORDER_FOR_TYPE.ONE_MONTH, label: "One month" },
  { value: ORDER_FOR_TYPE.MULTIPLE_MONTHS, label: "Multiple months" },
]

export const currentYearMonth = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
}

// BE wants the 2nd of the month as a plain date so no timezone shifts it into
// the previous/next month.
export const orderForDateOf = (yearMonth) => `${yearMonth}-02`

export const yearMonthOfOrderForDate = (date) => (typeof date === "string" ? date.slice(0, 7) : "")

// "YYYY-MM…" → "MM/YYYY"
const monthYearLabel = (date) => {
  const [y, m] = yearMonthOfOrderForDate(date).split("-")
  return y && m ? `${m}/${y}` : ""
}

// Month an Order-for line belongs to: BE's LinePeriod label, else fiscalMonth/fiscalYear.
export const linePeriodLabel = (l) =>
  l?.period?.label || (l?.fiscalMonth ? `${String(l.fiscalMonth).padStart(2, "0")}/${l.fiscalYear}` : "")

export const LINE_PERIOD_COLOR = {
  OVER_BUDGET: "var(--pc-danger, #b42318)",
  UNALLOCATED: "var(--pc-warn, #b54708)",
}

export const formatOrderFor = (type, dates = []) => {
  const typeLabel = ORDER_FOR_OPTIONS.find((o) => o.value === type)?.label
  if (!typeLabel) return "—"
  const months = (dates || []).map(monthYearLabel).filter(Boolean).join(", ")
  return months ? `${typeLabel} — ${months}` : typeLabel
}

// Derives Region from a center-code prefix (HN-* → North, HCM-* → South,
// DN-* → Central) since pro.budget_center has no region column today.
// ASSUMPTION: needs backend confirmation that center codes stay on this
// convention — product decision to derive it this way made 2026-08-13.
export const regionFromCenterCode = (code = "") => {
  if (code.startsWith("HN")) return "North"
  if (code.startsWith("HCM")) return "South"
  if (code.startsWith("DN")) return "Central"
  return "—"
}

export const CATEGORY_OPTIONS = [
  { label: "IT Equipment", value: "it_equipment" },
  { label: "Office Supplies", value: "office_supplies" },
  { label: "Facilities & Maintenance", value: "facilities" },
  { label: "Marketing & Events", value: "marketing" },
  { label: "Teaching Materials", value: "teaching_materials" },
  { label: "Professional Services", value: "services" },
]

// Order Request lifecycle stages used by My Orders end-to-end tracking.
export const ORDER_STAGE_OPTIONS = [
  { label: "Order", value: "order" },
  { label: "Purchase", value: "purchase" },
  { label: "Goods Receipt", value: "goods_receipt" },
  { label: "Payment", value: "payment" },
]

// Approval-rule entities (admin config) and the whitelisted condition fields.
export const RULE_ENTITY_OPTIONS = [
  { label: "Order", value: "order" },
  { label: "Purchase", value: "purchase" },
  { label: "Payment", value: "payment" },
]

export const RULE_CONDITION_FIELDS = [
  { label: "Category", value: "category" },
  { label: "Region", value: "region" },
  { label: "Center", value: "center" },
  { label: "Department", value: "department" },
  { label: "Amount", value: "amount" },
  { label: "Urgency", value: "urgency" },
]

// "amount" and "category" send a numeric value (amount is a magnitude,
// category is the real budget_category.id) — everything else compares as
// text (codes, enum values). Drives the `valueType` sent to the
// approval-rules API on every condition — must match ApprovalRuleConditionDTO
// exactly ("NUMBER" | "TEXT").
export const NUMERIC_CONDITION_FIELDS = ["amount", "category"]
export const valueTypeFor = (field) =>
  NUMERIC_CONDITION_FIELDS.includes(field) ? "NUMBER" : "TEXT"

export const RULE_CONDITION_OPERATORS = [
  { label: "=", value: "=" },
  { label: "≠", value: "!=" },
  { label: "≥", value: ">=" },
  { label: "≤", value: "<=" },
  { label: ">", value: ">" },
  { label: "<", value: "<" },
  { label: "In list", value: "in" },
]

// operator "in" shares ONE field, comma-separated — "1,2,5", never a JSON
// array or "[1,2,5]". Splitting/joining on "," happens in RuleEditor.js.
export const IN_OPERATOR_VALUE_SEPARATOR = ","

export const APPROVAL_MODE_OPTIONS = [
  { label: "One at a time", value: "sequential" },
  { label: "All at once", value: "parallel" },
]

// Approval-step "Approver" choices. The role options map to a fixed platform
// role (approver_type = "role", approver_ref = the role code); "Email" opens a
// free-text field so the admin can list one or more approver emails
// (approver_type = "emails", approver_ref = the comma-separated string).
export const APPROVER_ROLE_OPTIONS = [
  { label: "Procurement Manager", value: "ROLE_PROCUREMENT_MANAGER", type: "role" },
  { label: "Procurement", value: "ROLE_PROCUREMENT", type: "role" },
  { label: "Finance Team", value: "ROLE_FINANCE", type: "role" },
  { label: "All Center Managers (role)", value: "ROLE_CENTRE_MANAGER", type: "role" },
]

// Resolved per document by BE from the rule's center/department condition, so
// the rule must carry that condition ("=" or "in"). approverRef = the type
// itself (BE's default).
export const APPROVER_DYNAMIC_OPTIONS = [
  { label: "Center Manager", value: "center_manager", type: "center_manager", conditionField: "center" },
  { label: "Department Manager", value: "department_manager", type: "department_manager", conditionField: "department" },
]

export const APPROVER_EMAILS_OPTION = { label: "Email", value: "emails", type: "emails" }

export const APPROVER_OPTIONS = [...APPROVER_ROLE_OPTIONS, ...APPROVER_DYNAMIC_OPTIONS, APPROVER_EMAILS_OPTION]

export const findApproverOption = (step) =>
  step.approverType === "role"
    ? APPROVER_ROLE_OPTIONS.find((o) => o.value === step.approverRef)
    : APPROVER_OPTIONS.find((o) => o.type === step.approverType)

export const dynamicApproverOption = (approverType) =>
  APPROVER_DYNAMIC_OPTIONS.find((o) => o.type === approverType)

export const APPROVAL_MODE_LABELS = {
  sequential: "One at a time",
  parallel: "All at once",
}

export const approvalModeLabel = (mode) =>
  APPROVAL_MODE_LABELS[mode] || APPROVAL_MODE_LABELS.sequential

// Plain-language quorum text, e.g. "needs 1 approver(s)".
export const formatQuorum = (n) => `needs ${n || 1} approver${(n || 1) > 1 ? "s" : ""}`

// Per-step approval limit label for the resolved-chain / approval-progress
// UIs, e.g. "Duyệt tối đa: 50.000.000 ₫". `maxAmount` is nullable — null
// means unlimited, so callers should skip rendering entirely rather than
// show "0" or "Unlimited".
export const formatMaxAmount = (maxAmount) =>
  maxAmount == null ? null : `Approve up to: ${formatVND(maxAmount)}`

// Matches the 403 body the approve action returns when the document amount
// exceeds the acting approver's per-step limit, e.g.:
//   "Approver role:ROLE_PROCUREMENT_MANAGER can approve up to 50000000;
//    document amount 80000000 exceeds this limit"
// Distinct from the generic "not an approver" 403 — callers should check
// this first and fall back to the generic message when it doesn't match.
const APPROVAL_LIMIT_EXCEEDED_RE = /can approve up to\s+([\d.,]+).*exceeds this limit/i

export const approvalLimitExceededMessage = (rawMessage) => {
  const match = APPROVAL_LIMIT_EXCEEDED_RE.exec(rawMessage || "")
  if (!match) return null
  const limit = Number(match[1].replace(/[.,]/g, ""))
  return `You don't have authority to approve documents above ${formatVND(limit)}.`
}

// A vendor proposal row can exist (supplier invited) with items whose
// proposedAmount is still null — that is "no quote yet", not a 0 đồng quote.
export const NO_QUOTE_LABEL = "Chưa có báo giá"
export const quotedItems = (p) => (p?.items || []).filter((it) => it.proposedAmount != null)
export const hasQuote = (p) => quotedItems(p).length > 0
export const proposalTotal = (p) => quotedItems(p).reduce((sum, it) => sum + (Number(it.proposedAmount) || 0), 0)

// A quote row is either a catalog item (itemId) or an off-catalog order line
// (orderRequestLineId) — never both, the BE 400s otherwise. These give one key
// for matching order lines to proposal items, and the matching wire field.
export const proposalLineKey = (x) =>
  x?.offCatalog || (x?.itemId == null && x?.orderRequestLineId != null)
    ? `line-${x.orderRequestLineId}`
    : `item-${x?.itemId}`
export const proposalLineRef = (x) =>
  proposalLineKey(x).startsWith("line-") ? { orderRequestLineId: x.orderRequestLineId } : { itemId: x.itemId }

// Contract-price ("giá nguyên tắc") auto proposal: an order whose every line
// is a catalog item with isApplyReferencePrice from one default vendor needs
// no RFQ. Returns that vendor id, or null when the order doesn't qualify.
export const referencePriceVendorId = (orderLines = []) => {
  if (!orderLines.length || !orderLines.every((l) => l.itemId != null && l.isApplyReferencePrice === true)) return null
  const vendorIds = [...new Set(orderLines.map((l) => l.defaultVendorId))]
  return vendorIds.length === 1 && vendorIds[0] != null ? vendorIds[0] : null
}

// `items` for that proposal, one per itemId, priced from the vendor's still-
// valid price (GET /suppliers/{id}/item-prices, priceFound = VALID). null when
// any item has none — an estimate is never sent as a quote.
export const referencePriceItems = (orderLines = [], prices = []) => {
  const validTotal = new Map(
    (prices || []).filter((r) => r.priceFound).map((r) => [String(r.itemId), Number(r.totalPrice)])
  )
  const itemIds = [...new Set(orderLines.map((l) => String(l.itemId)))]
  if (!itemIds.length || !itemIds.every((key) => validTotal.has(key))) return null
  return itemIds.map((key) => ({ itemId: Number(key), proposedAmount: validTotal.get(key) }))
}

// OR detail lines[] or public .../lines rows → the rows the proposal panel and
// RFQ drawer quote against. Off-catalog lines are kept, one row per line id.
export const toProposalLines = (lines = []) =>
  lines
    .filter((l) => (l.offCatalog ? (l.lineId ?? l.id) != null : l.itemId != null && l.itemName))
    .map((l) => {
      const orderRequestLineId = l.offCatalog ? l.lineId ?? l.id : undefined
      const uom = l.uom || l.offCatalogUnit
      return {
        key: proposalLineKey({ itemId: l.itemId, orderRequestLineId, offCatalog: l.offCatalog }),
        itemId: l.offCatalog ? null : l.itemId,
        orderRequestLineId,
        offCatalog: !!l.offCatalog,
        itemName: l.itemName || l.offCatalogItemName,
        itemCode: l.itemCode,
        quantity: l.quantity,
        uom,
        unit: uom,
      }
    })
// PO approve needs a selected proposal with EVERY item priced.
export const isFullyQuoted = (p) => (p?.items || []).length > 0 && quotedItems(p).length === p.items.length

// `priceSource` per proposal item: who entered the price (null = no price yet).
// Tax code the vendor quoted on a proposal (BE caps it at 20 chars).
export const PROPOSAL_TAX_CODE_MAX = 20

// Quote-line warranty (warrantyMonths / warrantyNote), e.g. "BH 24 tháng — Chính hãng".
export const formatWarranty = (it) => {
  const months = it?.warrantyMonths != null ? `BH ${it.warrantyMonths} tháng` : null
  return [months, it?.warrantyNote].filter(Boolean).join(" — ") || null
}

// "" / null = not filled in (BE keeps the saved value); otherwise a whole number ≥ 0.
export const isValidWarrantyMonths = (v) => v === "" || v == null || (Number.isInteger(Number(v)) && Number(v) >= 0)

export const PRICE_SOURCE_LABEL = { STAFF: "Tự nhập báo giá", VENDOR: "Vendor", AUTO: "Giá nguyên tắc" }

// Price-readiness row (GET /purchases|order-requests/{id}/price-readiness):
// why a MISSING row has no price.
export const MISSING_PRICE_REASON_LABEL = {
  NO_PRICE: "NCC này chưa từng báo giá cho item",
  PRICE_EXPIRED: "Báo giá của NCC đã quá 1 năm — cần báo giá lại",
  ESTIMATE_PRICE: "Giá ước tính / ngoài danh mục — cần NCC báo giá",
  NO_QUANTITY: "Dòng chưa có số lượng",
}
export const unpricedRows = (readiness) => (readiness?.rows || []).filter((r) => r.status !== "PRICED")

// BE locks a PO's proposals (select / create / vendor update → 409) once it is
// past these statuses.
export const PO_PROPOSAL_EDITABLE_STATUSES = ["draft", "pending", "info_requested"]

// Staff-typed price vs an order-level quote for the same item: warn (soft) if
// it differs from every such quote by more than this many VND.
export const PRICE_DEVIATION_WARN_VND = 500000

// VND currency with thousands separators, consistent across procurement pages.
// List views round to whole đồng; pass the exact string via title/tooltip.
export const formatVND = (n) => formatVnd(n)
export const formatVNDExact = (n) => formatVndExact(n)

// Plain thousands-separated number (no currency suffix) for compact cells.
export const formatAmount = (n) => {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—"
  return Math.round(Number(n)).toLocaleString("vi-VN")
}

// Signed VND, e.g. "+6.000.000 ₫" — used for vendor-quote deltas vs the cheapest.
export const formatVNDSigned = (n) => {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—"
  const v = Number(n)
  const sign = v > 0 ? "+" : v < 0 ? "-" : ""
  return `${sign}${Math.abs(v).toLocaleString("vi-VN")} ₫`
}

// Date as DD/MM/YYYY. Never print a raw ISO string.
export const formatDate = (value) => formatDateDisplay(value)

// Date + time as DD/MM/YYYY · HH:mm. Never print a raw ISO string.
export const formatDateTime = (value) => formatDateTimeDisplay(value)

// GET /order-requests (and possibly /order-requests/my-orders) returns a
// mixed list — each item carries `type: "order" | "group"`. An "order" item
// is the same flat OrderRequestSummaryResponse shape as before; a "group"
// item has its status/totalAmount/budgetCategoryId DERIVED from its child
// `orders[]` (budgetCategoryId/Name is null when the children don't all
// share one category — a group isn't forced to be homogeneous like a PO is).
// Normalizes both into one row shape so list/table code doesn't have to
// branch on `type` everywhere. `orders` is null for a plain order row.
// `purchases` is always an array (never null) — the PO(s) already linked to
// this order/group, e.g. [{id, code, status, totalAmount, vendorId,
// vendorName}]. A group's `orders[]` children each carry their own
// `purchases[]` too (an order can have a PO from before it was bundled).
export const normalizeOrderOrGroupItem = (o) => ({
  type: o.type === "group" ? "group" : "order",
  id: o.id,
  code: o.code,
  title: o.title,
  status: o.status,
  region: o.region,
  categoryId: o.budgetCategoryId,
  categoryName: o.budgetCategoryName,
  total: o.totalAmount,
  urgency: o.urgency,
  requesterName: o.requesterName,
  createdAt: o.createdAt,
  updatedAt: o.updatedAt || o.createdAt,
  allowedActions: o.allowedActions || [],
  purchases: o.purchases || [],
  orders: o.orders || null,
  partiallyApproved: !!o.partiallyApproved,
  // Snapshot from the last submit; drafts are always false.
  overBudget: !!o.overBudget,
  overBudgetAmount: o.overBudgetAmount ?? null,
  // Only on the Create PO picker (excludeWithActivePurchase=true): lines not
  // yet on a live PO, same shape as OR detail lines[].
  availableLines: o.availableLines || [],
})

// "Payment Sequence" — the user-facing name for what the wire calls
// `batchLabel` (still a free-text string server-side). Renders as
// "Payment 1", "Payment 2", … — never "Batch".
const SEQUENCE_ORDINALS = [
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
]

export const paymentSequenceLabel = (n) => `Payment ${n}`

export const paymentSequenceOrdinal = (n) =>
  SEQUENCE_ORDINALS[n - 1] ? `${SEQUENCE_ORDINALS[n - 1]} payment` : `Payment #${n}`

// A previously-created batch counts as "already requested" for sequence
// numbering purposes regardless of its current approval status — the number
// just reflects how many payment requests already exist against this PO.
export const nextPaymentSequenceNumber = (existingCount = 0) => (Number(existingCount) || 0) + 1

// One line in the Payment Request create/edit form (F3) = one payment batch.
// Bank fields default to the Purchase's default bank (F2 context) when the
// line is added; `documents` holds staged File objects for this batch only.
// What POST /payment-requests/{id}/documents accepts (400 otherwise).
export const PAYMENT_DOCUMENT_ACCEPT = ".xml,.pdf,.jpg,.jpeg,.png,.xlsx,.docx"
export const PAYMENT_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024

export const documentTypeOf = (file) => {
  const ext = (file?.name || "").split(".").pop().toLowerCase()
  if (ext === "xml") return "invoice_xml"
  if (ext === "pdf") return "invoice_pdf"
  return "other"
}

export const newPaymentBatchLine = (defaults = {}) => ({
  batchLabel: "",
  paymentType: "normal",
  amount: "",
  paymentTimeline: "",
  bankReceiverName: defaults.bankReceiverName || "",
  bankNameBranch: defaults.bankNameBranch || "",
  bankAccount: defaults.bankAccount || "",
  invoiceSerial: "",
  invoiceNumber: "",
  invoiceDate: "",
  supplierTaxCode: "",
  description: "",
  note: "",
  documents: [],
})

export const newPaymentLine = () => ({
  supplierId: null,
  supplierName: "",
  invoiceSerial: "",
  invoiceNumber: "",
  invoiceDate: "",
  supplierTaxCode: "",
  description: "",
  amount: 0,
  note: "",
  paymentType: "normal",
  paymentTimeline: "",
  receiverName: "",
  bankName: "",
  bankAccount: "",
})
